import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import type { IMenuItemProps } from '../../../Menu.types';
import { createEventListeners } from './utils';
import { useMenuContext } from '../../../contexts/MenuContext';
import classes from './style/MenuItem.module.scss';
import { MenuParts } from '../../../constants';
import {
  MENU_ITEM_ANIMATION_CONFIG,
  INITIAL_ANIMATION_STATE,
  ANIMATION_PACKAGE_CONFIG,
} from '../../../animations';
import { MenuItemLabel } from './MenuItemLabel';
import { isEmptyObject } from '../../../../../../utils/dataUtils';
import { useAnimationState } from './useAnimationState';
import { useDropdown } from './useDropdown';
import { DropdownContext } from '../../../contexts/DropdownContext';
import { Dropdown } from '../../Dropdown';

export const MenuItem: React.FC<IMenuItemProps> = props => {
  const {
    item,
    currentItem,
    focusOnMenuItem,
    getAnimationPackage,
    partToPreviewStateMap,
    submenuDirection,
    onItemClick,
    onItemMouseIn,
    onItemMouseOut,
    onItemDblClick,
    submenuToggleIdPrefix,
  } = props;
  const { forceOpen = false } = item;

  const { animationState, initEnterAnimation, initExitAnimation } =
    useAnimationState(MENU_ITEM_ANIMATION_CONFIG, INITIAL_ANIMATION_STATE);

  const hasSubItems = !!item.items?.length;
  const hasContainer = !!item.isContainerItem;
  const hasDropdownMenu = hasContainer || hasSubItems;
  const hasLink = !isEmptyObject(item.link);

  const submenuToggleId =
    submenuToggleIdPrefix && hasSubItems
      ? `${submenuToggleIdPrefix}-${item.id}-submenu-toggle`
      : undefined;
  const submenuCheckboxRef = useRef<HTMLInputElement>(null);

  const prehydrationOpenRef = useRef<boolean | null>(null);
  if (prehydrationOpenRef.current === null) {
    if (submenuToggleId && typeof document !== 'undefined') {
      const checkbox = document.getElementById(
        submenuToggleId,
      ) as HTMLInputElement | null;
      prehydrationOpenRef.current = !!checkbox?.checked;
    } else {
      prehydrationOpenRef.current = false;
    }
  }

  const chevronButtonRef = useRef<HTMLButtonElement>(null);

  const itemRef = useRef<HTMLLIElement>(null);

  const getMenuItemAnimationName = () => {
    const animationPackage = getAnimationPackage();
    const menuItemAnimationName =
      ANIMATION_PACKAGE_CONFIG[animationPackage].menuItemAnimationName;

    return menuItemAnimationName;
  };

  const {
    isOpen,
    dropdownAnimationState,
    showDropdown,
    showDropdownImmediate,
    hideDropdown,
    toggleDropdown,
    updateDropdownDomStyles,
  } = useDropdown({
    itemRef,
    forceOpen,
    getAnimationPackage,
  });

  const { isHamburgerMenuOpened } = useMenuContext();

  const { setDropdownDomStylesApplier, dropdownElementRef } =
    useContext(DropdownContext);
  setDropdownDomStylesApplier(updateDropdownDomStyles);

  const eventListeners = createEventListeners({
    showDropdown,
    hideDropdown,
    itemRef,
    getMenuItemAnimationName,
    initEnterAnimation,
    initExitAnimation,
    isOpen,
    hasLink,
    dropdownAnimationState,
  });

  useEffect(() => {
    if (currentItem?.id === item.id && itemRef.current) {
      focusOnMenuItem?.(itemRef.current);
    }
  }, [currentItem, item, focusOnMenuItem]);

  useLayoutEffect(() => {
    if (
      (submenuCheckboxRef.current?.checked || prehydrationOpenRef.current) &&
      !isOpen
    ) {
      showDropdownImmediate();
    }

    if (hasDropdownMenu && !isOpen && itemRef.current?.matches(':hover')) {
      showDropdownImmediate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prevHamburgerOpenRef = useRef(isHamburgerMenuOpened);
  useEffect(() => {
    if (prevHamburgerOpenRef.current && !isHamburgerMenuOpened && isOpen) {
      hideDropdown();
    }
    prevHamburgerOpenRef.current = isHamburgerMenuOpened;
  }, [isHamburgerMenuOpened, isOpen, hideDropdown]);

  const handleEscKeyDown = useCallback(() => {
    chevronButtonRef.current?.focus();
    hideDropdown();
  }, [hideDropdown, chevronButtonRef]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleEscKeyDown();
      }
    },
    [handleEscKeyDown],
  );

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    } else {
      window.removeEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  const isElementInView = (element: HTMLLIElement) => {
    const boundingRect = element.getBoundingClientRect();
    return boundingRect.top >= 0 && boundingRect.bottom <= window.innerHeight;
  };

  const scrollIntoViewIfNeeded = () => {
    const element = itemRef.current;

    if (element && !isElementInView(element)) {
      element.scrollIntoView();
    }
  };

  const sharedDropdownProps = {
    item,
    currentItem,
    dropdownAnimationState,
    onEscKeyDown: handleEscKeyDown,
    onItemClick,
    onItemMouseIn,
    onItemMouseOut,
    onItemDblClick,
  };

  return (
    <li
      ref={itemRef}
      className={classes.listItem}
      data-part={MenuParts.MenuItem}
      data-animation-name={animationState.name}
      data-animation-state={animationState.phase}
      data-item-depth="0"
      onFocus={scrollIntoViewIfNeeded}
    >
      {submenuToggleId && (
        <input
          type="checkbox"
          id={submenuToggleId}
          ref={submenuCheckboxRef}
          data-submenu-toggle=""
          aria-hidden="true"
          tabIndex={-1}
          autoComplete="off"
          className={classes.hiddenToggle}
        />
      )}
      <div className={classes.itemWrapper} {...eventListeners}>
        <MenuItemLabel
          {...props}
          hasDropdownMenu={hasDropdownMenu}
          hasSubItems={hasSubItems}
          hasContainer={hasContainer}
          chevronButtonRef={chevronButtonRef}
          toggleDropdown={toggleDropdown}
          isOpen={isOpen}
          dropdownAnimationState={dropdownAnimationState}
          submenuToggleId={submenuToggleId}
        />
        {hasDropdownMenu && (
          <Dropdown
            {...sharedDropdownProps}
            menuOrientation="horizontal"
            ref={dropdownElementRef}
            direction={submenuDirection}
          />
        )}
      </div>
      {hasSubItems && (
        <Dropdown
          {...sharedDropdownProps}
          menuOrientation="vertical"
          isOpen={isOpen}
          partToPreviewStateMap={partToPreviewStateMap}
          direction={submenuDirection}
        />
      )}
      <span className={classes.divider}></span>
    </li>
  );
};
